/**
 * «Что спрашивают» (`/keywords`, `GET /api/kc/keywords`): где за период звучали слова — в звонках
 * (по расшифровкам) и в переписке «Чатов». Просьба руководителя КЦ 10.10.26: «сводку звонков и
 * сообщений по ключевым словам, например вб и вайлдбериз, чтобы вручную не искать и понять, что
 * интересует людей». Спека `docs/specs/2026-10-14-kc-keywords-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { MessageSquare, Phone } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { CLIENT_CHATS, has } from "@/lib/access";
import { apiFetch, query } from "@/lib/api";
import { iso, plusDays } from "@/lib/dates";
import { NoAccess } from "@/components/NoAccess";
import { useGetIdentity } from "@refinedev/core";
import type { CurrentUser } from "@/lib/providers";

type Item = {
  kind: "call" | "message";
  id: number;
  chat_id?: number;
  at: string;
  out: boolean;
  auto?: boolean;
  phone: string;
  duration?: number;
  channel?: string;
  group?: boolean;
  name?: string;
  user: string;
  deal_id?: number | null;
  lead_id?: number | null;
  text: string;
  hits: number;
};
type Data = {
  from: string;
  to: string;
  words: string[];
  client_only: boolean;
  totals: { calls: number; messages: number; client: number; groups: number; chats: number; call_phones: number };
  days: { day: string; calls: number; messages: number }[];
  by_word: { word: string; calls: number; messages: number }[];
  items: Item[];
  shown: number;
  found: number;
};

const ПО_УМОЛЧАНИЮ = "вб, вайлдберриз, вайлдбериз, wildberries";
const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;
const hhmm = (at: string) => at.slice(11, 16);

const load = (words: string, from: string, to: string, client: boolean) =>
  apiFetch<Data>(`/api/kc/keywords${query({ words, from, to, client: client ? 1 : "" })}`);

export function KeywordsPage() {
  const { data: me } = useGetIdentity<CurrentUser>();
  const [p, setP] = useSearchParams();
  const words = p.get("words") ?? ПО_УМОЛЧАНИЮ;
  const to = p.get("to") || iso(new Date());
  const from = p.get("from") || iso(plusDays(new Date(), -13));
  const client = p.get("client") === "1";
  const [draft, setDraft] = useState(words);
  const set = (next: Record<string, string>) => {
    const out = new URLSearchParams(p);
    for (const [k, v] of Object.entries(next)) v ? out.set(k, v) : out.delete(k);
    setP(out, { replace: true });
  };
  const r = useQuery({ queryKey: ["kc-keywords", words, from, to, client], queryFn: () => load(words, from, to, client) });
  if (me && !has(me, CLIENT_CHATS)) return <NoAccess what="Что спрашивают" />;
  const d = r.data;
  const max = Math.max(1, ...(d?.days ?? []).map((x) => x.calls + x.messages));
  return (
    <div className="flex flex-col gap-4" data-annot="что спрашивают">
      <header className="flex flex-col gap-1">
        <h1 className="text-[17px] font-medium">Что спрашивают</h1>
        <p className="text-[12.5px] text-fg-muted">
          Слова ищем целиком и без учёта регистра — в расшифровках звонков и в переписке «Чатов». Своя база,
          CRM не трогаем.
        </p>
      </header>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          set({ words: draft.trim() });
        }}
        data-annot="слова и период"
      >
        <label className="flex min-w-[18rem] flex-1 flex-col gap-1 text-[12.5px] text-fg-muted">
          Слова через запятую
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="вб, вайлдберриз"
            className="h-9 rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 text-[13px] text-fg"
          />
        </label>
        <DateRangePicker label="Период" from={from} to={to} onChange={(a, b) => set({ from: a, to: b })} />
        <label className="flex h-9 items-center gap-2 text-[12.5px] text-fg-muted">
          <input type="checkbox" checked={client} onChange={(e) => set({ client: e.target.checked ? "1" : "" })} />
          только сообщения клиентов
        </label>
        <button type="submit" className="h-9 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:border-fg">
          Искать
        </button>
      </form>

      {r.isPending && <div className="text-[12.5px] text-fg-muted">Ищу по звонкам и переписке…</div>}
      {r.isError && <div className="text-[12.5px] text-err">Не получилось: {String(r.error.message)}</div>}
      {d && (
        <>
          <section className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-3" data-annot="итоги слов">
            {[
              { label: "Звонков", value: d.totals.calls, hint: `${d.totals.call_phones} номеров` },
              { label: "Сообщений", value: d.totals.messages, hint: `${d.totals.chats} чатов` },
              { label: "Писали клиенты", value: d.totals.client, hint: "их сообщения" },
              { label: "В группах", value: d.totals.groups, hint: "чаты сотрудников" },
            ].map((c) => (
              <div key={c.label} className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-3 py-2">
                <div className="text-[11.5px] text-fg-faint">{c.label}</div>
                <div className="text-[20px] font-semibold tabular-nums">{c.value}</div>
                <div className="text-[11.5px] text-fg-muted">{c.hint}</div>
              </div>
            ))}
          </section>

          <section className="flex flex-wrap gap-4">
            <div className="min-w-[16rem] flex-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="по словам">
              <h2 className="border-b border-border px-3 py-2 text-[13px] font-medium">По словам</h2>
              <table className="w-full text-[12.5px]">
                <thead className="text-left text-[11.5px] text-fg-faint">
                  <tr>
                    <th className="px-3 py-1.5 font-medium">Слово</th>
                    <th className="px-3 py-1.5 text-right font-medium">Звонки</th>
                    <th className="px-3 py-1.5 text-right font-medium">Сообщения</th>
                  </tr>
                </thead>
                <tbody>
                  {d.by_word.map((w) => (
                    <tr key={w.word} className="border-t border-border">
                      <td className="px-3 py-1.5">{w.word}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{w.calls || "—"}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{w.messages || "—"}</td>
                    </tr>
                  ))}
                  {d.by_word.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-3 py-2 text-fg-muted">
                        за период слов не было
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="min-w-[18rem] flex-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="по дням">
              <h2 className="border-b border-border px-3 py-2 text-[13px] font-medium">По дням</h2>
              <div className="flex flex-col gap-0.5 px-3 py-2">
                {d.days.map((x) => (
                  <div key={x.day} className="flex items-center gap-2 text-[12px]">
                    <span className="w-10 tabular-nums text-fg-muted">{dm(x.day)}</span>
                    <span className="flex h-3 flex-1 items-center gap-0.5">
                      <span className="h-3 bg-[var(--color-ok)]" style={{ width: `${(x.calls / max) * 100}%` }} />
                      <span className="h-3 bg-[var(--color-accent)]" style={{ width: `${(x.messages / max) * 100}%` }} />
                    </span>
                    <span className="w-16 text-right tabular-nums text-fg-muted">
                      {x.calls} / {x.messages}
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-border px-3 py-1.5 text-[11.5px] text-fg-faint">
                зелёное — звонки, синее — сообщения
              </div>
            </div>
          </section>

          <section className="flex flex-col gap-2" data-annot="находки">
            <h2 className="text-[13px] font-medium">
              Находки <span className="text-fg-muted">— показано {d.shown} из {d.found}</span>
            </h2>
            {d.items.map((it) => (
              <article key={`${it.kind}-${it.id}`} className="rounded-[var(--radius-card)] border border-border bg-bg-subtle px-3 py-2">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12px] text-fg-muted">
                  {it.kind === "call" ? <Phone size={13} className="text-ok" /> : <MessageSquare size={13} className="text-accent" />}
                  <span className="tabular-nums">
                    {dm(it.at)} {hhmm(it.at)}
                  </span>
                  {it.kind === "call" ? (
                    <>
                      <span>{it.out ? "исходящий" : "входящий"}</span>
                      <span className="tabular-nums">{it.duration} с</span>
                      {it.phone && <Link to={`/search?q=${it.phone}`} className="font-mono hover:underline">{it.phone}</Link>}
                      {it.user && <span>· {it.user}</span>}
                      <Link to={`/calls/${it.id}`} className="ml-auto hover:underline">
                        звонок
                      </Link>
                    </>
                  ) : (
                    <>
                      <span>{it.channel}</span>
                      <span>{it.group ? "группа сотрудников" : it.out ? "мы" : "клиент"}</span>
                      <span>{it.name || it.phone}</span>
                      {it.user && <span>· {it.user}</span>}
                      <Link to={`/chats/${it.chat_id}`} className="ml-auto hover:underline">
                        чат
                      </Link>
                    </>
                  )}
                </div>
                <p className="mt-0.5 whitespace-pre-wrap text-[13px]">{it.text}</p>
              </article>
            ))}
            {d.items.length === 0 && <p className="text-[12.5px] text-fg-muted">Ничего не нашлось — попробуйте другие слова или период.</p>}
          </section>
        </>
      )}
    </div>
  );
}
